/**
 * Chat v1.5 — shared vocabulary.
 *
 * The sibling of `v1_5/Header/theme.ts`, and it follows the same contract: the design system is
 * light-only, so a dark-first component cannot read the global tokens directly. Every v1.5 chat
 * part takes a `theme` prop, applies the class from `ChatTheme.module.css`, and reads only
 * `--cv15-*` / `--hv15-*` custom properties. Light is a token swap, nothing else.
 *
 * These unions are deliberately small and closed. Where the existing `PlanningChat/ui` widgets each
 * declared their own near-identical status enum — five spellings of the same three states, needing
 * a mapper function every time two widgets talked to each other — the chat declares one here and
 * every part imports it.
 */

import { cn } from '~/lib/utils';
import headerThemes from '../Header/HeaderTheme.module.css';
import chatThemes from './ChatTheme.module.css';

/** Same contract as `HeaderTheme`: light is a token swap, nothing else. */
export type ChatTheme = 'dark' | 'light';

/**
 * Applies BOTH theme layers.
 *
 * The chat reuses the header's `--hv15-*` ramp wholesale — text tones, motion curves, durations —
 * rather than restating it under a second name, because a curve that differs between the bar and
 * the panel below it is a bug, not a variant. `--cv15-*` adds only what the chat genuinely
 * introduces (accents, the two shadows, the wider fill ramp).
 *
 * Every chat component's root does `className={cn(chatThemeClass(theme), styles.root, className)}`.
 * Custom properties inherit, so a themed composite themes every atom inside it for free.
 */
export function chatThemeClass(theme: ChatTheme = 'dark'): string {
  return cn(headerThemes[theme], chatThemes[theme]);
}

/**
 * Which tier of the 20px activity line is being drawn.
 *
 * The single most-repeated object in the design: six of the nine Paper columns drew this same row
 * under six different names. The three tiers differ only in glyph size and label tone —
 * `open` (16px glyph, 14/20 label) is a row you can still act on, `collapsed` (15px, 13/20) is one
 * that has finished and folded away, `live` is `open` with the dot matrix and a running label.
 */
export type ActivityDensity = 'open' | 'collapsed' | 'live';

/** Icon and label move together; the meta on the right never does — it stays at hint. */
export type ActivityTone = 'neutral' | 'muted' | 'success' | 'error';

/** Per-item state inside an `ActivityList`. */
export type ItemState = 'done' | 'running' | 'pending';

/**
 * The lifecycle one agent action walks through, from the eight `Agent state` artboards.
 *
 * This is the union the old code spelled three ways at once (`GoalWidgetStatus`,
 * `CanvasWidgetStatus`, `OutlineWidgetStatus`). Every state here is reachable from the stream and
 * each draws distinct chrome, so none can be dropped.
 */
export type AgentLifecycle =
  | 'running'
  | 'complete'
  | 'accepted'
  | 'rejected'
  | 'updating'
  | 'failed'
  | 'deleting'
  | 'deleted';

/** What sits under an activity row. `none` is a bare line with nothing below it. */
export type ActivityBodyKind =
  | 'none'
  | 'list'
  | 'skeleton'
  | 'thumbnails'
  | 'question'
  | 'explanation';

/**
 * Tool identity. Drives the leading glyph and nothing else — a Linear row and a Notion row are the
 * same component with a different mark, which is why the fourteen `Agent tool` artboards collapse
 * to one card rather than fourteen widgets.
 */
export type ActivityTool =
  | 'artifact'
  | 'document'
  | 'canvas-edit'
  | 'brand-kit'
  | 'outline'
  | 'goal'
  | 'research'
  | 'image'
  /* The three image TOOLS, distinct from `image` (which reads the library): a picture with an up
     arrow for a file joining the library, frame corners for a picture landing in a slot, a picture
     with a minus for one leaving a slot or the library. */
  | 'image-add'
  | 'image-place'
  | 'image-remove'
  | 'clock'
  | 'chat'
  | 'agent'
  /* The agent's own memory, split by VERB the way the image tools are — writing to it is a check,
     reading from it is an eye (Beka, 2026-09-20). They were all `agent` until then, which meant the
     dot matrix, which meant no mark at all once the row settled. Five members rather than three so
     each row keeps its own door: two of them happen to share a drawing today, and a point card that
     later earns its own is one line here instead of a split. */
  | 'point-card-set'
  | 'point-card-read'
  | 'stranger-read'
  | 'skill'
  /* The settled `Thought for N sec` recap. Not `agent`: that mark is the model working, and a
     finished thought is the one row guaranteed not to be. */
  | 'thinking'
  | 'linear'
  | 'figma'
  | 'notion'
  | 'github';

/** Where a mention points. */
export type MentionSource = 'figma' | 'artifact' | 'document' | 'web' | 'composition' | 'page';

/** Model vendor. The MARK is per-vendor; the label beside it is a free string. */
export type ModelVendor = 'claude' | 'google';

/** The composer's send affordance. `stop` is the same 32px slot holding a 15px square. */
export type SendState = 'idle' | 'active' | 'stop';

/** The three composer widths the `Chat/Responsive` artboard proves. */
export type ComposerWidth = 320 | 400 | 520;

/** The three menus the composer owns. */
export type ChatMenu = 'add' | 'mention' | 'model';

/** Rows of the composer's add menu. */
// `model` is gone: the model is chosen from the model chip's own menu, which is where the effort
// and the list live too. A second door onto it from the `+` meant the same setting had two homes
// and the `+` menu was answering a question it does not own.
export type AddMenuItem = 'images' | 'files' | 'website' | 'artifact' | 'apps';

/** Reasoning effort, offered in the model picker. */
export type Effort = 'low' | 'medium' | 'high';

/**
 * User bubble lifecycle. `editing` is orthogonal to this and is its own boolean prop — a queued
 * message and a sent message can both be under the caret.
 */
export type BubbleKind = 'sent' | 'queued';

/**
 * Which side of the thread a message-actions row belongs to. The row is one component; the side
 * decides how many glyphs it holds and how far apart (assistant: four at 16px, gap 14;
 * user: two at 15px, gap 12).
 */
export type MessageSide = 'user' | 'assistant';
